'use client'

import { appConfig } from './appConfig'
import { createAppConfigContext } from 'app-config-utils/client-side/createAppConfigContext'
import type { ComponentProps } from 'react'

const [Provider, useAppConfigContext] = createAppConfigContext(appConfig)

/**
 * Read the active app config from React context. Returns the per-request dynamic
 * config provided by the layout (`getDynamicAppConfig`), falling back to the
 * static build-time config outside a provider. Prefer this over importing the
 * static `appConfig` directly in client components that must reflect live store
 * settings (logo, theme, labels, etc.).
 */
export { useAppConfigContext }

/**
 * Wrapper around the context provider created by `createAppConfigContext`.
 *
 * This extra component is necessary because JSX and React (especially with Next.js)
 * require component types to be statically analyzable. Directly exporting a component
 * from array destructuring (e.g., `export const [AppConfigContextProvider] = ...`)
 * does not work with React's rendering pipeline and causes runtime errors.
 *
 * Always wrap the provider like this before using it in JSX.
 */
export const AppConfigContextProvider = ({ value, children }: ComponentProps<typeof Provider>) => {
  return <Provider value={value}>{children}</Provider>
}
